import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input, Modal, Select, Switch } from 'antd';
import { ArrowLeftOutlined, CopyOutlined, EditOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import { toast } from 'react-toastify';
import NginxService from '../http/NginxService';
import { NginxDomainConf } from '../entities/NginxDomainConf';
import { IS_TEST } from '../utils/config';
import MwsEnvironmentSelector from '../components/MwsEnvironmentSelector';
import './ConfListPage.css';

const { Option } = Select;

const ConfListPage = (): React.JSX.Element => {
    document.title = 'Nginx配置文件列表';
    const [form] = Form.useForm<NginxDomainConf>();
    const [nginxConfs, setNginxConfs] = useState<NginxDomainConf[]>([]);
    const [isNewSiteModalVisible, setIsNewSiteModalVisible] = useState(false);
    const [isConfModalVisible, setIsConfModalVisible] = useState(false);
    const [isMainConfModalVisible, setIsMainConfModalVisible] = useState(false);
    const envOptions = IS_TEST ? ['test', 'dev'] : ['prod', 'staging'];
    const [showCdnSite, setShowCdnSite] = useState(false);
    const [viewContent, setViewContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [rootDomainNames, setRootDomainNames] = useState<string[]>([]);
    const [selectedRootDomainName, setSelectedRootDomainName] = useState<string>('');
    const navigate = useNavigate();

    useEffect(() => {
        NginxService.listRootDomainNames().then((domains) => {
            if (domains) {
                setRootDomainNames(domains);
            }
        });
    }, []);

    useEffect(() => {
        NginxService.listConfs(selectedRootDomainName || undefined).then((confs) => {
            if (confs) {
                setNginxConfs(confs);
            }
        });
    }, [selectedRootDomainName]);

    const showMainConf = () => {
        NginxService.getMainConf().then((conf) => {
            if (!conf) {
                return;
            }
            setViewContent(conf.content);
            setIsMainConfModalVisible(true);
        });
    };

    const confirmEditMainConf = async () => {
        NginxService.updateMainConf(viewContent).then((response) => {
            if (response.isFailed()) {
                return;
            }
            toast.success("主站配置更新成功！");
            cancelMainConfViewer();
        });
    };

    const clickConf = (nginxConf: NginxDomainConf) => {
        navigate(`/conf/edit/${nginxConf.domainName}`, { state: { conf: nginxConf } });
    };

    const addSite = () => {
        form.setFieldsValue({ environment: envOptions[0] });
        setIsNewSiteModalVisible(true);
    };

    const createDomainConf = async () => {
        setIsLoading(true);
        const newConf = await form.validateFields();
        await NginxService.createConf(newConf)
            .then((response) => {
                if (response.isFailed()) {
                    return;
                }
                toast.success('新增站点成功！');
                window.location.reload();
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const onSiteChange = (values: any) => {
        const containsCdn = values.domainName?.includes('cdn.');
        if (containsCdn !== showCdnSite) {
            setShowCdnSite(containsCdn);
        }
    };

    const viewConf = (content: string) => {
        setViewContent(content);
        setIsConfModalVisible(true);
    };

    const copyViewContent = async () => {
        if (!viewContent) {
            toast.error('没有可复制内容');
            return;
        }
        try {
            await navigator.clipboard.writeText(viewContent);
            toast.success('复制成功');
        } catch {
            toast.error('复制失败');
        }
    };

    const cancelMainConfViewer = () => {
        setIsMainConfModalVisible(false);
        setViewContent('');
        setEditMode(false);
    };

    return (
        <div className="conf-list-page">
            <div className="conf-list-header">
                <div className="header-title">
                    <Button type="link" style={{ color: 'black' }} icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} />
                    <span className="title-text">Nginx配置</span>
                    <MwsEnvironmentSelector prefix="nginx" enableDevHost={true} />
                    <Select className="root-domain-selector" value={selectedRootDomainName} onChange={setSelectedRootDomainName}>
                        <Option value="">全部站点</Option>
                        {rootDomainNames.map((domain) => (
                            <Option key={domain} value={domain}>
                                {domain}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div className="header-actions">
                    <Button onClick={showMainConf}>主站配置</Button>
                    <Button color="blue" variant="outlined" onClick={addSite}>
                        新增站点
                    </Button>
                </div>
            </div>

            {nginxConfs.length === 0 ? (
                <p>没有配置</p>
            ) : (
                <div className="conf-list">
                    {nginxConfs.map((conf) => (
                        <div key={conf.domainName} className="conf-item">
                            <div className="conf-item-title">
                                {conf.locked && <span className="locked">已锁定</span>}
                                <span className="title">{conf.domainName}</span>
                            </div>
                            <div className="conf-item-actions">
                                <Button onClick={() => viewConf(conf.content)}>查看</Button>
                                <Button type="default" color="blue" variant="outlined" onClick={() => clickConf(conf)}>
                                    编辑
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal
                title="新增站点"
                open={isNewSiteModalVisible}
                onCancel={() => setIsNewSiteModalVisible(false)}
                onOk={createDomainConf}
                cancelText="取消"
                okText="确认"
                confirmLoading={isLoading}
            >
                <Form form={form} layout="horizontal" onValuesChange={onSiteChange} labelCol={{ style: { width: '100px' } }}>
                    <Form.Item label="站点名称" name="domainName" required>
                        <Input placeholder="站点名称，例如 xxx.haiskynology.com" />
                    </Form.Item>
                    <Form.Item label="环境" name="environment">
                        <Select>
                            {envOptions.map((env) => (
                                <Option key={env} value={env}>
                                    {env}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {showCdnSite && (
                        <Form.Item label="是否为CDN站点" name="cdnSite" valuePropName="checked" initialValue={false}>
                            <Switch />
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            <Modal
                width="75%"
                title={
                    <div className="conf-modal-header">
                        <span>查看配置</span>
                        <Button size="small" type="text" className="conf-copy-btn" icon={<CopyOutlined />} onClick={copyViewContent} aria-label="复制配置" />
                    </div>
                }
                open={isConfModalVisible}
                onCancel={() => setIsConfModalVisible(false)}
                footer={null}
            >
                <pre>{viewContent}</pre>
            </Modal>

            <Modal
                width="75%"
                wrapClassName="main-conf-modal"
                title={
                    <div className="main-conf-modal-header">
                        <span>主站配置</span>
                        <div className="main-conf-modal-actions">
                            <Button
                                size="small"
                                type="text"
                                className="conf-copy-btn"
                                icon={<CopyOutlined />}
                                onClick={copyViewContent}
                                aria-label="复制主站配置"
                            />
                            <EditOutlined style={{ cursor: 'pointer', fontSize: '16px' }} onClick={() => setEditMode(!editMode)} />
                        </div>
                    </div>
                }
                open={isMainConfModalVisible}
                okText="提交修改"
                cancelText="取消"
                onCancel={cancelMainConfViewer}
                onOk={confirmEditMainConf}
                footer={editMode ? undefined : null}
            >
                {editMode ? <TextArea value={viewContent} onChange={(e) => setViewContent(e.target.value)} rows={30} /> : <pre>{viewContent}</pre>}
            </Modal>
        </div>
    );
};

export default ConfListPage;
